import { AfterViewInit, Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { DataManagementService } from 'src/app/pages/data-management/data-management.service';
import { RealtimeWorkerService } from 'src/app/pages/realtime/realtime-worker.service';
import { MapService } from 'src/app/utils/leaflet/service/map.service';
import { StateService, ZoomState } from 'src/app/utils/leaflet/service/state.service';

@Component({
  selector: 'app-draggable-sheet',
  templateUrl: './draggable-sheet.component.html',
  styleUrls: ['./draggable-sheet.component.scss'],
})
export class DraggableSheetComponent implements OnInit, AfterViewInit {
  @Input() minHeight = 96;
  @Input() startExpanded = false;
  /** When true with startExpanded, open at full max height (not the mid 60% snap). */
  @Input() startMaximized = false;
  @Input() showClose = false;
  @Input() showBottomNav = false;
  @Input() historiqueActive = false;
  // When set, the close (X) button navigates to this route; otherwise it just collapses.
  @Input() closeRoute: string | null = null;
  @Output() closed = new EventEmitter<void>();

  readonly navBarHeight = 84;
  maxHeight = 500;
  height = 96;
  dragging = false;
  contentVisible = true;

  private pointerActive = false;
  private startY = 0;
  private startHeight = 0;

  constructor(
    private router: Router,
    public realTimeWorker: RealtimeWorkerService,
    public dataManagementService: DataManagementService,
    private mapService: MapService,
    private stateService: StateService,
  ) {}

  ngOnInit() {
    this.readSafeAreaBottom();
    this.computeBounds();
    this.contentVisible = this.startExpanded || this.startMaximized;
    if (this.startMaximized) {
      this.height = this.maxHeight;
    } else if (this.startExpanded) {
      this.height = Math.round(this.maxHeight * 0.6);
    } else {
      this.height = this.collapsedHeight;
    }
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.computeBounds();
      if (this.startMaximized) {
        this.expandMaximized();
      }
    }, 0);
  }

  /** Expand the pull-up to the maximum available height and show content. */
  expandMaximized(): void {
    this.computeBounds();
    this.contentVisible = true;
    this.height = this.maxHeight;
  }

  private readSafeAreaBottom(): void {
    if (typeof window === 'undefined' || !window.getComputedStyle) {
      return;
    }
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue('--ion-safe-area-bottom')
      .trim();
    const parsed = parseInt(value, 10);
    this.safeAreaBottom = Number.isFinite(parsed) ? parsed : 0;
  }

  get collapsedHeight(): number {
    return this.minHeight;
  }

  get footerNavOffset(): number {
    return this.showBottomNav
      ? this.navBarHeight + this.safeAreaBottom
      : 0;
  }

  private safeAreaBottom = 0;

  @HostListener('window:resize')
  computeBounds() {
    this.readSafeAreaBottom();
    const footerEl = document.querySelector('.ds-shell .ds-footer-nav .rt-bottom-nav') as HTMLElement | null;
    const measuredNav = footerEl?.offsetHeight ?? this.navBarHeight;
    const navTotal = this.showBottomNav
      ? measuredNav + this.safeAreaBottom
      : 0;
    const available = window.innerHeight - navTotal;
    this.maxHeight = Math.max(
      Math.round(available * 0.88),
      this.minHeight + 120
    );
    if (this.height > this.maxHeight) {
      this.height = this.maxHeight;
    }
  }

  onDragStart(ev: MouseEvent | TouchEvent) {
    this.pointerActive = true;
    this.dragging = false;
    this.startY = this.getPointerY(ev);
    this.startHeight = this.height;
  }

  @HostListener('document:mousemove', ['$event'])
  @HostListener('document:touchmove', ['$event'])
  onDragMove(ev: MouseEvent | TouchEvent) {
    if (!this.pointerActive) {
      return;
    }
    const delta = this.startY - this.getPointerY(ev);
    if (Math.abs(delta) > 3) {
      this.dragging = true;
    }
    let height = this.startHeight + delta;
    height = Math.min(this.maxHeight, Math.max(this.collapsedHeight, height));
    this.height = height;
    // Keep historique data visible while dragging; only the diamond button collapses content.
  }

  @HostListener('document:mouseup')
  @HostListener('document:touchend')
  onDragEnd() {
    if (!this.pointerActive) {
      return;
    }
    this.pointerActive = false;
    setTimeout(() => (this.dragging = false), 0);
  }

  private getPointerY(ev: MouseEvent | TouchEvent): number {
    if (ev instanceof MouseEvent) {
      return ev.clientY;
    }
    const touch = ev.touches[0] || ev.changedTouches[0];
    return touch ? touch.clientY : 0;
  }

  close(ev?: Event) {
    if (ev) {
      ev.stopPropagation();
    }
    this.closed.emit();
    if (this.showBottomNav) {
      this.contentVisible = false;
      return;
    }
    if (this.closeRoute) {
      this.router.navigate([this.closeRoute]);
    } else {
      this.height = this.collapsedHeight;
    }
  }

  onCenterMenuClick(): void {
    if (!this.showBottomNav) {
      return;
    }
    this.contentVisible = !this.contentVisible;
    if (this.contentVisible) {
      this.height = Math.max(this.height, Math.round(this.maxHeight * 0.6));
    }
  }

  isVehicleFocused(): boolean {
    return this.realTimeWorker.trackingMode && !!this.dataManagementService.selectedVehicule;
  }

  getSelectedVehicleLabel(): string {
    const vehicule = this.dataManagementService.selectedVehicule;
    return vehicule ? this.dataManagementService.getVehiculeName(vehicule) : '';
  }

  clearVehicleSelection(): void {
    this.mapService.map?.closePopup();
    this.realTimeWorker.resetForGlobalView();
    this.stateService.setSelectedMarker(null);
    this.stateService.updateState(ZoomState.Default);
  }
}
